import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { checkIn, checkOut, fetchAttendance, fetchCorrections, fetchHrSettings, fetchLeave, recordAttendance, requestCorrection, requestLeave, reviewCorrection, reviewLeave } from "../../api/HrAPIs";
import { Empty, Field, HrFrame, HrGuard, StatusPill, day, inputClass, useHr } from "./hrUi";

export default function AttendancePage() {
  return <HrGuard anyOf={["HR.ViewAttendance", "HR.ManageAttendance", "HR.ApproveLeaves"]}><Body /></HrGuard>;
}

function Body() {
  const hr = useHr();
  const queryClient = useQueryClient();
  const attendance = useQuery({ queryKey: ["hrAttendance"], queryFn: () => fetchAttendance({}) });
  const corrections = useQuery({ queryKey: ["hrCorrections"], queryFn: () => fetchCorrections("Pending") });
  const leave = useQuery({ queryKey: ["hrLeave"], queryFn: () => fetchLeave() });
  const settings = useQuery({ queryKey: ["hrSettings"], queryFn: fetchHrSettings });
  const [manual, setManual] = useState({ employeeId: "", workDate: "", checkInAtUtc: "", checkOutAtUtc: "", absent: false, reason: "" });
  const [leaveForm, setLeaveForm] = useState({ leaveTypeId: "", startDate: "", endDate: "", reason: "" });

  const run = async (work) => {
    try { await work(); toast.success("تم"); queryClient.invalidateQueries({ queryKey: ["hrAttendance"] }); queryClient.invalidateQueries({ queryKey: ["hrLeave"] }); queryClient.invalidateQueries({ queryKey: ["hrCorrections"] }); }
    catch (error) { toast.error(error.message); }
  };

  return (
    <HrFrame title="الحضور والإجازات" subtitle="وقت الحضور يُسجل من السيرفر. الموظف لا يعدّل الختم الأصلي مباشرة.">
      <div className="mb-4 flex flex-wrap gap-2">
        <button type="button" className="rounded-xl bg-emerald-600 px-4 py-2 text-sm text-white" onClick={() => run(checkIn)}>تسجيل حضور</button>
        <button type="button" className="rounded-xl bg-slate-800 px-4 py-2 text-sm text-white" onClick={() => run(checkOut)}>تسجيل انصراف</button>
        <button type="button" className="rounded-xl border px-4 py-2 text-sm" onClick={() => run(() => requestCorrection({ requestType: "MissedCheckout", reason: "نسيان الانصراف" }))}>طلب تصحيح</button>
      </div>
      <div className="grid gap-2">
        {(attendance.data || []).map((row) => (
          <div key={row.id} className="flex items-center justify-between rounded-2xl border bg-white p-3 text-sm">
            <span>{row.employeeName} · {day(row.workDate)}</span>
            <StatusPill value={row.status} />
          </div>
        ))}
        {(attendance.data || []).length === 0 && <Empty text="لا توجد سجلات في هذا الشهر." />}
      </div>

      <h2 className="mb-2 mt-6 font-bold">طلب إجازة</h2>
      <form className="mb-4 grid gap-2 md:grid-cols-2" onSubmit={(event) => { event.preventDefault(); run(() => requestLeave({ ...leaveForm, leaveTypeId: Number(leaveForm.leaveTypeId) })); }}>
        <Field label="النوع">
          <select className={inputClass} required value={leaveForm.leaveTypeId} onChange={(event) => setLeaveForm({ ...leaveForm, leaveTypeId: event.target.value })}>
            <option value="">اختر</option>
            {(settings.data?.leaveTypes || []).map((type) => <option key={type.id} value={type.id}>{type.name}</option>)}
          </select>
        </Field>
        <Field label="من"><input className={inputClass} type="date" required value={leaveForm.startDate} onChange={(event) => setLeaveForm({ ...leaveForm, startDate: event.target.value })} /></Field>
        <Field label="إلى"><input className={inputClass} type="date" required value={leaveForm.endDate} onChange={(event) => setLeaveForm({ ...leaveForm, endDate: event.target.value })} /></Field>
        <Field label="السبب"><input className={inputClass} value={leaveForm.reason} onChange={(event) => setLeaveForm({ ...leaveForm, reason: event.target.value })} /></Field>
        <button className="rounded-xl bg-indigo-600 px-4 py-2 text-sm text-white">إرسال</button>
      </form>
      {(leave.data || []).map((row) => (
        <div key={row.id} className="mb-2 flex flex-wrap items-center justify-between gap-2 rounded-2xl border bg-white p-3 text-sm">
          <span>{row.employeeName} · {row.leaveType} · {day(row.startDate)} → {day(row.endDate)}</span>
          <span className="flex items-center gap-2">
            <StatusPill value={row.status} />
            {row.status === "Pending" && hr.can("HR.ApproveLeaves") && (
              <>
                <button type="button" className="text-emerald-700" onClick={() => run(() => reviewLeave(row.id, { approve: true }))}>اعتماد</button>
                <button type="button" className="text-rose-700" onClick={() => run(() => reviewLeave(row.id, { approve: false }))}>رفض</button>
              </>
            )}
          </span>
        </div>
      ))}

      {hr.can("HR.ManageAttendance") && (
        <form className="mt-6 grid gap-2 rounded-2xl border bg-white p-4" onSubmit={(event) => { event.preventDefault(); run(() => recordAttendance({ ...manual, employeeId: Number(manual.employeeId), checkInAtUtc: manual.checkInAtUtc ? new Date(manual.checkInAtUtc).toISOString() : null, checkOutAtUtc: manual.checkOutAtUtc ? new Date(manual.checkOutAtUtc).toISOString() : null })); }}>
          <h2 className="font-bold">تسجيل يدوي</h2>
          <Field label="رقم الموظف"><input className={inputClass} required value={manual.employeeId} onChange={(event) => setManual({ ...manual, employeeId: event.target.value })} /></Field>
          <Field label="اليوم"><input className={inputClass} type="date" required value={manual.workDate} onChange={(event) => setManual({ ...manual, workDate: event.target.value })} /></Field>
          <Field label="حضور"><input className={inputClass} type="datetime-local" value={manual.checkInAtUtc} onChange={(event) => setManual({ ...manual, checkInAtUtc: event.target.value })} /></Field>
          <Field label="انصراف"><input className={inputClass} type="datetime-local" value={manual.checkOutAtUtc} onChange={(event) => setManual({ ...manual, checkOutAtUtc: event.target.value })} /></Field>
          <label className="text-sm"><input type="checkbox" checked={manual.absent} onChange={(event) => setManual({ ...manual, absent: event.target.checked })} /> غياب</label>
          <Field label="السبب"><input className={inputClass} required value={manual.reason} onChange={(event) => setManual({ ...manual, reason: event.target.value })} /></Field>
          <button className="rounded-xl bg-slate-900 px-4 py-2 text-sm text-white">حفظ</button>
        </form>
      )}

      <h2 className="mb-2 mt-6 font-bold">تصحيحات معلّقة</h2>
      {(corrections.data || []).map((row) => (
        <div key={row.id} className="mb-2 flex items-center justify-between rounded-2xl border bg-white p-3 text-sm">
          <span>موظف {row.employeeId} · {row.reason}</span>
          {hr.can("HR.ManageAttendance") && <button type="button" className="text-indigo-600" onClick={() => run(() => reviewCorrection(row.id, { approve: true }))}>اعتماد</button>}
        </div>
      ))}
      {(corrections.data || []).length === 0 && <Empty text="لا توجد تصحيحات معلّقة." />}
    </HrFrame>
  );
}
